// De rijen achter een meter (Spoor B, sessie B2; endpointcontract B1 §11).
//
// "Klik op een meter → de rijen erachter" is in B1 bewust géén tweede endpoint
// geworden: elke meting draagt een `detail` met precies die rijen — de negen
// documenten die het zoeken miste, de trend die op één bron steunt, de
// verdeling per uitgeverstype, de controles die rood staan. Dit onderdeel
// tekent die vrije vorm.
//
// Vrije vorm betekent niet vormloos. Het schema zegt alleen "een object", dus
// hier wordt per waarde bekeken WAT het is en daarna hoe het leest:
//
//   een lijst tekst            → een opsomming
//   een lijst met titels       → een opsomming van de titels
//   een verdeling (naam→getal) → balken, grootste bovenaan
//   een tabel (naam→velden)    → een tabelletje
//   één getal, datum of woord  → een regel "label: waarde"
//
// Een sleutel die we niet kennen, toont zichzelf met streepjes als spaties
// (`detailLabel`) — kaal maar eerlijk, en dan hoort hij in `woorden.ts`. Wat
// hier NIET gebeurt: optellen, sorteren naar een oordeel, of een percentage
// uitrekenen dat de server niet gaf. De balken zijn getekend op de grootste
// waarde in dezelfde verdeling; hun label is altijd de telling zelf.

import type { ReactNode } from "react";

import { EmptyReason } from "../../components/Empty";
import type {
  MeterkastDetailKaart,
  MeterkastMeter,
  MeterkastMeting,
  MeterkastSectorwaarde,
  MeterkastSnede,
  MeterkastVergelijk,
} from "../../contracts";
import { color, size, weight } from "../../tokens";
import { Balken, cijfer, datum, getal } from "./BronnenStukken";
import { Oordeel } from "./MeterkastOordeel";
import { detailLabel, detailWaardeLabel } from "./woorden";

/** Sleutels die het detail wél draagt maar die elders al staan: de reden onder
 *  het stoplicht, en de run in de regel onder het blok. Twee keer hetzelfde
 *  tonen maakt een blok langer, niet duidelijker. */
const ELDERS = new Set(["reden", "run"]);

export function Detail({
  meter,
  vergelijk,
  huidig,
  basis,
  onGewijzigd,
}: {
  meter: MeterkastMeter;
  vergelijk: MeterkastVergelijk;
  huidig: MeterkastSnede;
  basis: MeterkastSnede;
  onGewijzigd: () => void;
}) {
  const meerdere = meter.waarden.length > 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Wanneer meter={meter} vergelijk={vergelijk} huidig={huidig} basis={basis} />

      {meter.waarden.map((w) => (
        <section key={w.sector}>
          {meerdere && (
            <h3
              style={{
                margin: "0 0 8px",
                fontSize: size.meta,
                fontWeight: weight.medium,
                color: color.ink,
              }}
            >
              {w.sector}
            </h3>
          )}
          <SectorDetail waarde={w} />
          {/* Bord 5 is mensenwerk: alleen daar staat een invoerveld, en alleen
              voor de meters die de server aanneemt (D1, D2). */}
          {(meter.meter === "D1" || meter.meter === "D2") && (
            <Oordeel
              meter={meter.meter}
              sector={w.sector}
              huidig={huidig}
              onGewijzigd={onGewijzigd}
            />
          )}
        </section>
      ))}

      {meter.drempel && (
        <p style={{ margin: 0, fontSize: size.meta, color: color.faint, lineHeight: 1.55 }}>
          {`Rood vanaf: ${meter.drempel}.`}
        </p>
      )}
    </div>
  );
}

/** Wanneer deze meter gemeten wordt, en welke twee standen je op dit moment
 *  naast elkaar ziet. Staat boven het detail, want het antwoord op "waarom is
 *  er geen vorige waarde" zit hier vaker in dan in de cijfers zelf. */
function Wanneer({
  meter,
  vergelijk,
  huidig,
  basis,
}: {
  meter: MeterkastMeter;
  vergelijk: MeterkastVergelijk;
  huidig: MeterkastSnede;
  basis: MeterkastSnede;
}) {
  const kant = (s: MeterkastSnede) =>
    vergelijk === "methode" ? `methode ${s.methode}` : (maandKort(s.period) ?? `methode ${s.methode}`);
  return (
    <p style={{ margin: 0, fontSize: size.meta, color: color.soft, lineHeight: 1.55 }}>
      {`Gemeten: ${meter.wanneer}. `}
      {basis.period || vergelijk === "methode"
        ? `Hier naast elkaar: ${kant(huidig)} en ${kant(basis)}.`
        : `Hier: ${kant(huidig)}; er is nog niets om naast te leggen.`}
    </p>
  );
}

function maandKort(iso: string | null): string | null {
  const d = datum(iso);
  return d ? d.replace(/^\d+\s/, "") : null;
}

function SectorDetail({ waarde }: { waarde: MeterkastSectorwaarde }) {
  const m = waarde.huidig;
  if (!m) {
    return <EmptyReason reason={waarde.reden ?? "hier is nog niets gemeten"} />;
  }
  const kaart = m.detail as MeterkastDetailKaart;
  const sleutels = Object.keys(kaart).filter((s) => !ELDERS.has(s));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {waarde.reden && (
        <p style={{ margin: 0, fontSize: size.meta, color: color.muted, lineHeight: 1.55 }}>
          {waarde.reden}
        </p>
      )}

      {sleutels.length === 0 ? (
        <EmptyReason reason="deze meter draagt geen rijen — alleen het getal zelf" />
      ) : (
        sleutels.map((s) => <Stuk key={s} sleutel={s} waarde={kaart[s]} />)
      )}

      <Herkomst meting={m} />
    </div>
  );
}

/** Eén sleutel uit het detail, in de vorm die bij zijn inhoud past. */
function Stuk({ sleutel, waarde }: { sleutel: string; waarde: unknown }) {
  const titel = detailLabel(sleutel);

  if (Array.isArray(waarde)) {
    if (waarde.length === 0) {
      return <Regel label={titel} waarde="geen" />;
    }
    return (
      <Blokje titel={`${titel} (${getal(waarde.length)})`}>
        <ul style={lijst}>
          {waarde.map((r, i) => (
            <li key={i} style={{ lineHeight: 1.55 }}>
              {rijTekst(r)}
            </li>
          ))}
        </ul>
      </Blokje>
    );
  }

  if (isKaart(waarde)) {
    const paren = Object.entries(waarde);
    if (paren.length === 0) return <Regel label={titel} waarde="geen" />;

    // Een verdeling: elke waarde is een getal. Balken lezen sneller dan een
    // rij komma's, en de grootste hoort bovenaan.
    if (paren.every(([, v]) => typeof v === "number")) {
      const rijen = paren
        .map(([k, v]) => ({ sleutel: k, label: detailWaardeLabel(k), n: v as number }))
        .sort((a, b) => b.n - a.n);
      const schaal = Math.max(...rijen.map((r) => r.n), 1);
      return (
        <Blokje titel={titel}>
          <Balken rijen={rijen} schaal={schaal} />
        </Blokje>
      );
    }

    // Een tabelletje: elke waarde is zelf een kaart met dezelfde velden.
    if (paren.every(([, v]) => isKaart(v))) {
      const kolommen = [
        ...new Set(paren.flatMap(([, v]) => Object.keys(v as MeterkastDetailKaart))),
      ];
      return (
        <Blokje titel={titel}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", fontSize: size.meta, minWidth: 260 }}>
              <thead>
                <tr>
                  <th style={tabelKop} />
                  {kolommen.map((k) => (
                    <th key={k} scope="col" style={{ ...tabelKop, textAlign: "right" }}>
                      {detailWaardeLabel(k)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paren.map(([naam, rij]) => (
                  <tr key={naam}>
                    <th scope="row" style={{ ...tabelCel, fontWeight: weight.regular }}>
                      {detailWaardeLabel(naam)}
                    </th>
                    {kolommen.map((k) => (
                      <td key={k} style={{ ...tabelCel, ...cijfer, textAlign: "right" }}>
                        {los((rij as MeterkastDetailKaart)[k])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Blokje>
      );
    }

    // Gemengd: dan elke sleutel als eigen regel, zodat niets wegvalt.
    return (
      <Blokje titel={titel}>
        <ul style={lijst}>
          {paren.map(([k, v]) => (
            <li key={k} style={{ lineHeight: 1.55 }}>
              {`${detailWaardeLabel(k)}: ${los(v)}`}
            </li>
          ))}
        </ul>
      </Blokje>
    );
  }

  return <Regel label={titel} waarde={los(waarde)} />;
}

/** Eén rij uit een lijst: tekst blijft tekst; een object toont zijn titel (en
 *  anders alles wat het heeft), nooit een kaal id. */
function rijTekst(rij: unknown): string {
  if (typeof rij === "string") return rij;
  if (isKaart(rij)) {
    const titel = rij.titel ?? rij.naam ?? rij.label;
    if (typeof titel === "string") return titel;
    return Object.entries(rij)
      .filter(([k]) => k !== "id")
      .map(([k, v]) => `${detailWaardeLabel(k)}: ${los(v)}`)
      .join(" · ");
  }
  return los(rij);
}

/** Eén losse waarde als tekst. Een datum leest als datum, een getal met
 *  Nederlandse duizendtallen, en al het andere zoals het staat. */
function los(waarde: unknown): string {
  if (waarde === null || waarde === undefined) return "—";
  if (typeof waarde === "number") {
    return Number.isInteger(waarde) ? getal(waarde) : waarde.toLocaleString("nl-NL");
  }
  if (typeof waarde === "boolean") return waarde ? "ja" : "nee";
  if (typeof waarde === "string") {
    return /^\d{4}-\d{2}-\d{2}/.test(waarde) ? (datum(waarde) ?? waarde) : waarde;
  }
  return JSON.stringify(waarde);
}

function isKaart(waarde: unknown): waarde is MeterkastDetailKaart {
  return typeof waarde === "object" && waarde !== null && !Array.isArray(waarde);
}

/** Waar deze meting vandaan komt: wanneer, uit welke run, en onder welk
 *  ijkpunt hij bevroren is. Zonder die regel is een getal niet na te lopen. */
function Herkomst({ meting }: { meting: MeterkastMeting }) {
  const run = (meting.detail as MeterkastDetailKaart).run;
  const delen = [
    datum(meting.gemetenOp) ? `gemeten op ${datum(meting.gemetenOp)}` : null,
    `run ${meting.runId}`,
    typeof run === "string" ? `oogstronde ${run}` : null,
    meting.label ? `ijkpunt "${meting.label}"` : null,
  ].filter((d): d is string => d !== null);
  return (
    <p style={{ margin: 0, fontSize: size.label, color: color.faint, lineHeight: 1.6 }}>
      {delen.join(" · ")}
    </p>
  );
}

function Blokje({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: size.label,
          fontWeight: weight.medium,
          color: color.soft,
          marginBottom: 7,
        }}
      >
        {titel}
      </div>
      {children}
    </div>
  );
}

function Regel({ label, waarde }: { label: string; waarde: string }) {
  return (
    <div style={{ fontSize: size.meta, color: color.body, lineHeight: 1.55 }}>
      <span style={{ color: color.soft }}>{`${label}: `}</span>
      <span style={cijfer}>{waarde}</span>
    </div>
  );
}

const lijst = {
  listStyle: "disc",
  paddingLeft: 20,
  margin: 0,
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontSize: size.meta,
  color: color.body,
} as const;

const tabelKop = {
  fontSize: size.label,
  fontWeight: weight.medium,
  color: color.faint,
  textAlign: "left",
  padding: "4px 12px 4px 0",
  borderBottom: `1px solid ${color.border}`,
  whiteSpace: "nowrap",
} as const;

const tabelCel = {
  padding: "4px 12px 4px 0",
  color: color.body,
  borderBottom: `1px solid ${color.lineFaint}`,
  whiteSpace: "nowrap",
} as const;
